import { useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { isVideoEligiblePlatform, type VideoGenerationDto } from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { useMediaItem } from "../content/useMediaBatch";
import { QueryKeys } from "../LinkGen";
import { ModalMakeVideo } from "./ModalMakeVideo";
import {
	formatMinutes,
	isVideoActive,
	useVideoActions,
	useVideoAvailability,
	useVideoGenerations,
} from "./useVideoGenerations";

const STATUS_LABEL: Record<VideoGenerationDto["status"], string> = {
	draft: "Draft",
	queued: "Waiting to start",
	thinking: "Planning scenes",
	reviewing: "Checking the script",
	generating: "Making your video",
	completed: "Ready",
	failed: "Didn't finish",
	cancelled: "Cancelled",
};

const NETWORK_LABELS: Record<string, string> = {
	instagram: "Instagram",
	tiktok: "TikTok",
	youtube: "YouTube",
	facebook: "Facebook",
	threads: "Threads",
	linkedin: "LinkedIn",
	x: "X",
};

/** Story 6.2: what a reviewer needs before approving the post. */
function FinishedVideo(props: { video: VideoGenerationDto }) {
	const { video } = props;
	const file = useMediaItem(video.contentFileId);
	const networks = video.targetNetworks.length
		? video.targetNetworks
		: video.targetPlatform
			? [video.targetPlatform]
			: [];
	return (
		<div className="mt-2">
			{file.data && (
				// biome-ignore lint/a11y/useMediaCaption: captions are burned into the render and HeyGen's subtitle file is SRT, which <track> can't play
				<video
					controls
					preload="metadata"
					playsInline
					poster={file.data.thumbnails["256"]}
					style={{
						maxHeight: 320,
						maxWidth: "100%",
						borderRadius: 8,
						background: "#000",
					}}
				>
					<source src={file.data.url} type="video/mp4" />
				</video>
			)}
			<div className="small text-muted mt-1">
				{video.actualSeconds ?? video.targetSeconds}s ·{" "}
				{video.orientation === "portrait" ? "Portrait" : "Landscape"} · Captions
				burned in
				{file.data?.subtitlesUrl ? " + subtitle file" : ""}
				{networks.length
					? ` · Publishes to ${networks.map((n) => NETWORK_LABELS[n] ?? n).join(", ")}`
					: ""}
			</div>
		</div>
	);
}

function VideoRow(props: {
	video: VideoGenerationDto;
	onRetry: (video: VideoGenerationDto) => void;
	onRegenerate: (video: VideoGenerationDto, reason: string) => void;
	busy: boolean;
}) {
	const { video, busy } = props;
	const [remaking, setRemaking] = useState(false);
	const [reason, setReason] = useState("");
	const active = isVideoActive(video);

	let detail: string | null = null;
	if (video.status === "queued") {
		detail =
			video.notice ??
			`${video.queuePosition ? `#${video.queuePosition} in line · ` : ""}ready in about ${formatMinutes(video.estimatedWaitSeconds ?? video.estimatedRenderSeconds)}`;
	} else if (active) {
		detail = `About ${formatMinutes(video.estimatedWaitSeconds ?? video.estimatedRenderSeconds)} left`;
	} else if (video.status === "failed" || video.status === "cancelled") {
		detail = video.failureMessage;
	}

	return (
		<li className="py-2 border-bottom">
			<div className="d-flex justify-content-between align-items-center gap-2">
				<div className="text-truncate">
					<strong>{video.title}</strong>
					<span className="text-muted"> · {STATUS_LABEL[video.status]}</span>
				</div>
				{(video.status === "failed" || video.status === "cancelled") && (
					<Button
						size="sm"
						variant="secondary"
						loading={busy}
						onClick={() => props.onRetry(video)}
					>
						Try again
					</Button>
				)}
				{video.status === "completed" && !remaking && (
					<Button
						size="sm"
						variant="secondary"
						onClick={() => setRemaking(true)}
					>
						Remake
					</Button>
				)}
			</div>
			{active && (
				<div
					className="progress mt-1"
					style={{ height: 6 }}
					role="progressbar"
					aria-valuenow={video.progress}
					aria-valuemin={0}
					aria-valuemax={100}
				>
					<div
						className="progress-bar progress-bar-striped progress-bar-animated"
						style={{ width: `${Math.max(video.progress, 5)}%` }}
					/>
				</div>
			)}
			{detail && <div className="small text-muted mt-1">{detail}</div>}
			{video.status === "completed" && video.contentFileId && (
				<FinishedVideo video={video} />
			)}
			{remaking && (
				<div className="mt-2">
					<Form.Label htmlFor={`video-remake-${video.id}`} className="small">
						What should change? (optional)
					</Form.Label>
					<ExtraForm.Control
						id={`video-remake-${video.id}`}
						value={reason}
						maxLength={300}
						onChange={(e) => setReason(e.target.value)}
						placeholder="e.g. Use a warmer voice and show the storefront"
					/>
					<div className="small text-muted mt-1">
						A new video uses more of your HeyGen credits.
					</div>
					<div className="d-flex gap-2 mt-2">
						<Button
							size="sm"
							variant="secondary"
							onClick={() => setRemaking(false)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							loading={busy}
							onClick={() => {
								props.onRegenerate(video, reason.trim());
								setRemaking(false);
								setReason("");
							}}
						>
							Make a new version
						</Button>
					</div>
				</div>
			)}
		</li>
	);
}

/** Story 6.1–6.3: "Make this a video" plus status, retry and remake for a post. */
export const PostVideoGenerations: FC<{
	postId: number;
	platform: string;
	canEdit: boolean;
	onVideoReady: (contentFileId: number) => void;
}> = ({ postId, platform, canEdit, onVideoReady }) => {
	const availability = useVideoAvailability();
	const eligible = isVideoEligiblePlatform(platform);
	const videos = useVideoGenerations(
		postId,
		availability.flagOn && eligible,
		5,
	);
	const { retry, regenerate } = useVideoActions();
	const { open } = useModal();
	const emit = useAlertEmit();
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();
	const wasActive = useRef(new Set<number>());

	useEffect(() => {
		for (const video of videos.data ?? []) {
			if (isVideoActive(video)) {
				wasActive.current.add(video.id);
			} else if (wasActive.current.has(video.id)) {
				wasActive.current.delete(video.id);
				if (video.status === "completed" && video.contentFileId) {
					onVideoReady(video.contentFileId);
					void queryClient.invalidateQueries({
						queryKey: QueryKeys.Files.All(companyId),
					});
					void queryClient.invalidateQueries({
						queryKey: QueryKeys.Posts.All(companyId),
					});
					emit({ type: AlertType.SUCCESS, content: "Your video is ready." });
				}
			}
		}
	}, [videos.data, onVideoReady, queryClient, companyId, emit]);

	if (!availability.flagOn || !eligible) {
		return null;
	}

	const list = videos.data ?? [];
	const anyActive = list.some(isVideoActive);
	const onError = (error: unknown) =>
		emit({
			type: AlertType.ERROR,
			content: parseError(error) ?? "Something went wrong. Try again.",
		});

	return (
		<div className="mt-3 p-3 border rounded bg-light">
			<Form.Label className="fw-semibold mb-1">Video</Form.Label>
			<Form.Text className="text-muted d-block mb-2">
				Turn this post into a short captioned video.
			</Form.Text>
			{list.length > 0 && (
				<ul className="list-unstyled mb-2">
					{list.map((video) => (
						<VideoRow
							key={video.id}
							video={video}
							busy={
								(retry.isPending && retry.variables?.id === video.id) ||
								(regenerate.isPending && regenerate.variables?.id === video.id)
							}
							onRetry={(v) => retry.mutate({ id: v.id }, { onError })}
							onRegenerate={(v, reason) =>
								regenerate.mutate({ id: v.id, reason }, { onError })
							}
						/>
					))}
				</ul>
			)}
			{availability.enabled ? (
				<Button
					icon="movie"
					variant="secondary"
					disabled={!canEdit || anyActive}
					onClick={() => open(ModalMakeVideo, { postId })}
				>
					Make this a video
				</Button>
			) : (
				availability.reason && (
					<div className="small text-muted">{availability.reason}</div>
				)
			)}
			{!canEdit && (
				<div className="small text-muted mt-1">
					This post was already published (or is publishing now), so it can't
					get a new video. Open a draft or scheduled post instead.
				</div>
			)}
		</div>
	);
};
